import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { isAppleOS } from '../../utils/checkOS';
import { Icons } from './Icons';

const ImageControls = ({ open, onClose }) => {
	const { t } = useTranslation();
	const isApple = isAppleOS();

	useEffect(() => {
		if (!open) return undefined;
		const onKey = (e) => { if (e.key === 'Escape') onClose(); };
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [open, onClose]);

	useEffect(() => {
		if (!open) return undefined;
		const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
		const prevOverflow = document.body.style.overflow;
		const prevPaddingRight = document.body.style.paddingRight;
		document.body.style.overflow = 'hidden';
		if (scrollbarWidth > 0) {
			document.body.style.paddingRight = `${scrollbarWidth}px`;
		}
		return () => {
			document.body.style.overflow = prevOverflow;
			document.body.style.paddingRight = prevPaddingRight;
		};
	}, [open]);

	if (!open || typeof document === 'undefined') return null;

	return createPortal(
		<>
			<div className='dn-modal-backdrop' onClick={onClose} aria-hidden='true' />
			<div
				className='dn-modal'
				role='dialog'
				aria-modal='true'
				aria-labelledby='dn-controls-title'
				onClick={(e) => e.stopPropagation()}
			>
				<div className='dn-modal-header'>
					<h2 className='dn-modal-title' id='dn-controls-title'>
						<Icons.keyboard /> {t('display.actionBtns.controls', { defaultValue: 'Keyboard shortcuts' })}
					</h2>
					<button type='button' className='dn-modal-close' onClick={onClose} aria-label='Close'>
						<Icons.close />
					</button>
				</div>
				<div className='dn-modal-body'>
					<div className='dn-shortcut-row'>
						<span className='dn-shortcut-label'>{t('controlsModal.zoom', { defaultValue: 'Zoom' })}</span>
						<span className='dn-shortcut-keys'>
							<kbd className='dn-kbd'>{isApple ? '⌘ Cmd' : 'Ctrl'}</kbd>
							<span className='dn-shortcut-plus'>+</span>
							<kbd className='dn-kbd'>{t('display.shortcuts.scroll', { defaultValue: 'Scroll wheel' })}</kbd>
						</span>
					</div>
					<div className='dn-shortcut-row'>
						<span className='dn-shortcut-label'>{t('controlsModal.move', { defaultValue: 'Move' })}</span>
						<span className='dn-shortcut-keys'>
							<kbd className='dn-kbd'>{isApple ? '⌥ Option' : 'Alt'}</kbd>
							<span className='dn-shortcut-plus'>+</span>
							<kbd className='dn-kbd'>{t('display.shortcuts.leftClick', { defaultValue: 'Left click' })}</kbd>
						</span>
					</div>
				</div>
			</div>
		</>,
		document.body
	);
};

ImageControls.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default ImageControls;
